/**
 * Data transformation utilities for converting database entities into D3.js graph format
 * Transforms the portfolio's relational data model into node-link graph structure
 */

import * as d3 from 'd3';
import { companies, activities, projects, certificates, certificateActivities } from '../../../db';

export interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  type: 'company' | 'activity' | 'project' | 'certificate' | 'skill';
  name: string;
  group: number;
  data: any;
  size: number;
  color: string;
}

export interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  type: 'company-activity' | 'activity-project' | 'activity-certificate' | 'project-skill' | 'certificate-skill';
  strength: number;
  color: string;
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
  statistics: {
    totalEntities: number;
    totalRelationships: number;
    entityCounts: Record<string, number>;
    relationshipCounts: Record<string, number>;
  };
}

/**
 * Color scheme for different entity types
 */
const COLORS = {
  company: '#3b82f6',     // Blue
  activity: '#10b981',    // Green  
  project: '#f59e0b',     // Amber
  certificate: '#8b5cf6', // Purple
  skill: '#ef4444',       // Red
  link: '#6b7280'         // Gray
} as const;

/**
 * Size scale for different entity types based on their importance
 */
const SIZES = {
  company: 20,
  activity: 15,
  project: 12,
  certificate: 10,
  skill: 8
} as const;

/**
 * Transform companies into graph nodes
 */
function transformCompaniesToNodes(): GraphNode[] {
  return companies.map(company => ({
    id: `company-${company.id}`,
    type: 'company' as const,
    name: company.name,
    group: 1,
    data: company,
    size: SIZES.company,
    color: COLORS.company
  }));
}

/**
 * Transform activities into graph nodes
 */
function transformActivitiesToNodes(): GraphNode[] {
  return activities.map(activity => ({
    id: `activity-${activity.id}`,
    type: 'activity' as const,
    name: activity.title,
    group: 2,
    data: activity,
    size: SIZES.activity,
    color: COLORS.activity
  }));
}

/**
 * Transform projects into graph nodes
 */
function transformProjectsToNodes(): GraphNode[] {
  return projects
    .filter(project => project.isVisible !== false) // Only include visible projects (default to visible if not specified)
    .map(project => ({
      id: `project-${project.id}`,
      type: 'project' as const,
      name: project.title,
      group: 3,
      data: project,
      size: SIZES.project,
      color: COLORS.project
    }));
}

/**
 * Transform certificates into graph nodes
 */
function transformCertificatesToNodes(): GraphNode[] {
  return certificates
    .filter(cert => cert.isVisible) // Only include visible certificates
    .map(certificate => ({
      id: `certificate-${certificate.id}`,
      type: 'certificate' as const,
      name: certificate.title,
      group: 4,
      data: certificate,
      size: SIZES.certificate,
      color: COLORS.certificate
    }));
}

/**
 * Extract unique skills from projects and certificates and transform into graph nodes
 */
function transformSkillsToNodes(): GraphNode[] {
  const skillSet = new Set<string>();
  
  // Extract skills from visible projects
  projects
    .filter(project => project.isVisible !== false)
    .forEach(project => {
      if (project.categorizedSkills) {
        // Handle all skill categories
        const allSkills = [
          ...(project.categorizedSkills.profession || []),
          ...(project.categorizedSkills.language || []),
          ...(project.categorizedSkills.tools || []),
          ...(project.categorizedSkills.lead || [])
        ];
        allSkills.forEach(skill => {
          if (typeof skill === 'object' && skill.name) {
            skillSet.add(skill.name);
          } else if (typeof skill === 'string') {
            skillSet.add(skill);
          }
        });
      }
    });
  
  // Extract skills from visible certificates
  certificates
    .filter(cert => cert.isVisible)
    .forEach(certificate => {
      if (certificate.skills) {
        certificate.skills.forEach(skill => skillSet.add(skill));
      }
    });
  
  // Convert to nodes
  return Array.from(skillSet).map((skillName, index) => ({
    id: `skill-${skillName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
    type: 'skill' as const,
    name: skillName,
    group: 5,
    data: { name: skillName },
    size: SIZES.skill,
    color: COLORS.skill
  }));
}

/**
 * Create links between companies and activities
 */
function createCompanyActivityLinks(): GraphLink[] {
  return activities.map(activity => ({
    source: `company-${activity.companyId}`,
    target: `activity-${activity.id}`,
    type: 'company-activity' as const,
    strength: 1,
    color: COLORS.link
  }));
}

/**
 * Create links between activities and projects
 * Note: Using the activityId field from projects as our relationship
 * Private projects (activityId === null) are not linked to activities
 */
function createActivityProjectLinks(): GraphLink[] {
  const links: GraphLink[] = [];
  
  projects
    .filter(project => project.isVisible !== false) // Only include visible projects
    .forEach(project => {
      // Skip private projects (activityId === null)
      if (project.activityId === null) {
        return;
      }
      
      // Handle both single activityId (number) and multiple activityIds (array)
      const activityIds = Array.isArray(project.activityId) ? project.activityId : [project.activityId];
      
      activityIds.forEach(activityId => {
        links.push({
          source: `activity-${activityId}`,
          target: `project-${project.id}`,
          type: 'activity-project' as const,
          strength: 0.8,
          color: COLORS.link
        });
      });
    });
  
  return links;
}

/**
 * Create links between activities and certificates
 */
function createActivityCertificateLinks(): GraphLink[] {
  return certificateActivities
    .filter(ca => {
      // Only include links to visible certificates
      const certificate = certificates.find(cert => cert.id === ca.certificateId);
      return certificate?.isVisible;
    })
    .map(ca => ({
      source: `activity-${ca.activityId}`,
      target: `certificate-${ca.certificateId}`,
      type: 'activity-certificate' as const,
      strength: 0.6,
      color: COLORS.link
    }));
}

/**
 * Create links between projects and skills
 */
function createProjectSkillLinks(): GraphLink[] {
  const links: GraphLink[] = [];
  
  projects
    .filter(project => project.isVisible !== false)
    .forEach(project => {
      if (project.categorizedSkills) {
        const allSkills = [
          ...(project.categorizedSkills.profession || []),
          ...(project.categorizedSkills.language || []),
          ...(project.categorizedSkills.tools || []),
          ...(project.categorizedSkills.lead || [])
        ];
        
        allSkills.forEach(skill => {
          const skillName = typeof skill === 'object' ? skill.name : skill;
          const skillId = `skill-${skillName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          
          links.push({
            source: `project-${project.id}`,
            target: skillId,
            type: 'project-skill' as const,
            strength: typeof skill === 'object' ? skill.weight * 0.5 : 0.3,
            color: COLORS.link
          });
        });
      }
    });
  
  return links;
}

/**
 * Create links between certificates and skills
 */
function createCertificateSkillLinks(): GraphLink[] {
  const links: GraphLink[] = [];
  
  certificates
    .filter(cert => cert.isVisible)
    .forEach(certificate => {
      if (certificate.skills) {
        certificate.skills.forEach(skillName => {
          const skillId = `skill-${skillName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          
          links.push({
            source: `certificate-${certificate.id}`,
            target: skillId,
            type: 'certificate-skill' as const,
            strength: 0.4,
            color: COLORS.link
          });
        });
      }
    });
  
  return links;
}

/**
 * Calculate statistics for the graph data
 */
function calculateStatistics(nodes: GraphNode[], links: GraphLink[]) {
  const entityCounts = nodes.reduce((acc, node) => {
    acc[node.type] = (acc[node.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const relationshipCounts = links.reduce((acc, link) => {
    acc[link.type] = (acc[link.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return {
    totalEntities: nodes.length,
    totalRelationships: links.length,
    entityCounts,
    relationshipCounts
  };
}

/**
 * Main function to transform the entire data model into D3.js graph format
 */
export function transformDataModelToGraph(): GraphData {
  // Transform all entities to nodes
  const companyNodes = transformCompaniesToNodes();
  const activityNodes = transformActivitiesToNodes();
  const projectNodes = transformProjectsToNodes();
  const certificateNodes = transformCertificatesToNodes();
  const skillNodes = transformSkillsToNodes();
  
  const nodes = [
    ...companyNodes,
    ...activityNodes,
    ...projectNodes,
    ...certificateNodes,
    ...skillNodes
  ];

  // Create all relationship links
  const companyActivityLinks = createCompanyActivityLinks();
  const activityProjectLinks = createActivityProjectLinks();
  const activityCertificateLinks = createActivityCertificateLinks();
  const projectSkillLinks = createProjectSkillLinks();
  const certificateSkillLinks = createCertificateSkillLinks();
  
  const links = [
    ...companyActivityLinks,
    ...activityProjectLinks,
    ...activityCertificateLinks,
    ...projectSkillLinks,
    ...certificateSkillLinks
  ];

  // Calculate statistics
  const statistics = calculateStatistics(nodes, links);

  return {
    nodes,
    links,
    statistics
  };
}

/**
 * Get entity counts for documentation/display purposes
 */
export function getEntityCounts() {
  // Calculate unique skills count
  const skillSet = new Set<string>();
  
  // Extract skills from visible projects
  projects
    .filter(project => project.isVisible !== false)
    .forEach(project => {
      if (project.categorizedSkills) {
        const allSkills = [
          ...(project.categorizedSkills.profession || []),
          ...(project.categorizedSkills.language || []),
          ...(project.categorizedSkills.tools || []),
          ...(project.categorizedSkills.lead || [])
        ];
        allSkills.forEach(skill => {
          const skillName = typeof skill === 'object' ? skill.name : skill;
          skillSet.add(skillName);
        });
      }
    });
  
  // Extract skills from visible certificates
  certificates
    .filter(cert => cert.isVisible)
    .forEach(certificate => {
      if (certificate.skills) {
        certificate.skills.forEach(skill => skillSet.add(skill));
      }
    });

  const skillsCount = skillSet.size;

  return {
    companies: companies.length,
    activities: activities.length,
    projects: projects.filter(project => project.isVisible !== false).length, // Only count visible projects
    certificates: certificates.filter(cert => cert.isVisible).length,
    skills: skillsCount,
    totalEntities: companies.length + activities.length + projects.filter(project => project.isVisible !== false).length + certificates.filter(cert => cert.isVisible).length + skillsCount
  };
}

/**
 * Get relationship descriptions for documentation
 */
export function getRelationshipDescriptions() {
  const projectActivityCount = projects
    .filter(project => project.isVisible !== false && project.activityId !== null) // Only count visible projects with activities
    .reduce((count, project) => {
      const activityIds = Array.isArray(project.activityId) ? project.activityId : [project.activityId];
      return count + activityIds.length;
    }, 0);

  const certificateActivityCount = certificateActivities.filter(ca => {
    const certificate = certificates.find(cert => cert.id === ca.certificateId);
    return certificate?.isVisible;
  }).length;

  // Count project-skill relationships
  let projectSkillCount = 0;
  projects
    .filter(project => project.isVisible !== false)
    .forEach(project => {
      if (project.categorizedSkills) {
        const allSkills = [
          ...(project.categorizedSkills.profession || []),
          ...(project.categorizedSkills.language || []),
          ...(project.categorizedSkills.tools || []),
          ...(project.categorizedSkills.lead || [])
        ];
        projectSkillCount += allSkills.length;
      }
    });

  // Count certificate-skill relationships
  let certificateSkillCount = 0;
  certificates
    .filter(cert => cert.isVisible)
    .forEach(certificate => {
      if (certificate.skills) {
        certificateSkillCount += certificate.skills.length;
      }
    });

  return {
    'Company → Activity': activities.length,
    'Activity ↔ Project': projectActivityCount,
    'Activity ↔ Certificate': certificateActivityCount,
    'Project ↔ Skill': projectSkillCount,
    'Certificate ↔ Skill': certificateSkillCount,
    totalRelationships: activities.length + projectActivityCount + certificateActivityCount + projectSkillCount + certificateSkillCount
  };
}
